import { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import Icon from './Icon';
import { CONTENT } from '../data/content/index';
import { ROUTE_META } from '../data/routeMeta';
import { getSectionScores, getLevel, getOverallLevel } from '../lib/scoring';
import { getRoute } from '../lib/routing';
import { COLORS, FONTS, TRACKING } from '../styles/tokens';
import { track } from '../lib/analytics';

// Tier colors come in two flavors: `bar` (vibrant — used for progress fills,
// chart bars, left accents) and `text` (a darker variant of the same hue that
// meets WCAG AA ≥4.5:1 on a white card surface). Pill backgrounds use `fill`,
// which is the bar color at ~8% opacity.

// Overall readiness tier — used for the hero block and overall score bar only.
const TIER = {
  strong:     { bar: COLORS.cyan,      text: '#0E7CB8', fill: 'rgba(0,173,239,0.08)',  label: 'Strong' },
  moderate:   { bar: COLORS.accent,    text: '#3B5BDB', fill: 'rgba(59,91,219,0.08)',  label: 'Moderate' },
  developing: { bar: COLORS.slateBlue, text: '#6D5BAA', fill: 'rgba(109,91,170,0.08)', label: 'Developing' },
};

// Per-dimension semantic colors — HIGH pain = red (problem), HIGH familiarity/clarity/readiness = teal (good).
// label: what the tier means in plain English for each dimension.
const DIM_SEMANTICS = {
  pain: {
    // High pain is bad — signals urgent operational need
    developing: { bar: '#2AC0D0', text: '#0891B2', fill: 'rgba(8,145,178,0.08)',   label: 'Low Pain' },
    moderate:   { bar: '#FBBF24', text: '#B45309', fill: 'rgba(180,83,9,0.08)',    label: 'Moderate Pain' },
    strong:     { bar: '#F87171', text: '#DC2626', fill: 'rgba(220,38,38,0.08)',   label: 'High Pain' },
  },
  familiarity: {
    developing: { bar: '#F87171', text: '#DC2626', fill: 'rgba(220,38,38,0.08)',   label: 'New to AI' },
    moderate:   { bar: '#FBBF24', text: '#B45309', fill: 'rgba(180,83,9,0.08)',    label: 'Growing' },
    strong:     { bar: '#2AC0D0', text: '#0891B2', fill: 'rgba(8,145,178,0.08)',   label: 'Fluent' },
  },
  clarity: {
    developing: { bar: '#F87171', text: '#DC2626', fill: 'rgba(220,38,38,0.08)',   label: 'Needs Mapping' },
    moderate:   { bar: '#FBBF24', text: '#B45309', fill: 'rgba(180,83,9,0.08)',    label: 'Partial Clarity' },
    strong:     { bar: '#2AC0D0', text: '#0891B2', fill: 'rgba(8,145,178,0.08)',   label: 'Clear Path' },
  },
  readiness: {
    developing: { bar: '#FBBF24', text: '#B45309', fill: 'rgba(180,83,9,0.08)',    label: 'Exploring' },
    moderate:   { bar: '#6B91FF', text: '#3B5BDB', fill: 'rgba(59,91,219,0.08)',   label: 'Almost Ready' },
    strong:     { bar: '#2AC0D0', text: '#0891B2', fill: 'rgba(8,145,178,0.08)',   label: 'Ready to Act' },
  },
};

const DIM_CONFIG = [
  { key: 'pain',        label: 'The Pain',               shortLabel: 'Pain',        max: 12 },
  { key: 'familiarity', label: 'AI Familiarity',          shortLabel: 'Familiarity', max: 12 },
  { key: 'clarity',     label: 'Clarity of Opportunity',  shortLabel: 'Clarity',     max: 12 },
  { key: 'readiness',   label: 'Ready to Act',            shortLabel: 'Readiness',   max: 8  },
];

const HERO_COPY = {
  strong: {
    headline: name => `${name}, you're ahead of most organizations.`,
    subtitle: n =>
      `You scored Strong on ${n} of four dimensions. The gap between where you are and your next level of leverage is small, and specific.`,
  },
  moderate: {
    headline: name => `${name}, you're building real momentum.`,
    subtitle: () =>
      "You have genuine strengths and clear areas to develop. There's meaningful leverage available when you know where to focus.",
  },
  developing: {
    headline: name => `${name}, here's where to focus first.`,
    subtitle: () =>
      "You're earlier in the journey than most organizations we work with. This report shows you exactly where to start.",
  },
};

function useIsNarrow() {
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 720 : false
  );
  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < 720);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return narrow;
}

// First two sentences from the start of a body string
function twoSentences(text) {
  const flat = text.split('\n\n')[0].replace(/\s+/g, ' ').trim();
  let count = 0;
  let result = '';
  for (let i = 0; i < flat.length; i++) {
    result += flat[i];
    if ('.!?'.includes(flat[i]) && (i + 1 >= flat.length || flat[i + 1] === ' ')) {
      count++;
      if (count === 2) break;
    }
  }
  return result.trim();
}

function firstParagraph(text) {
  return text.split('\n\n')[0];
}

// ── Report download ─────────────────────────────────────────────────────────

// Palette used inside the PDF — Collabo XD brand, light theme.
// `teal` holds CXD Cyan, `accent` holds CXD Navy, surfaces are light.
const PDF = {
  bg:       [240, 244, 248], // CXD Light Gray (page background)
  surface:  [255, 255, 255], // White (card fill)
  border:   [220, 226, 234], // subtle navy-tinted rule/stroke
  teal:     [0, 173, 239],   // CXD Cyan — primary accent
  accent:   [37, 47, 106],   // CXD Navy — strong accent
  textPri:  [37, 47, 106],   // CXD Navy (primary text)
  textSec:  [51, 65, 85],    // CXD Dark Gray (secondary text)
  textMut:  [148, 163, 184], // CXD Med Gray (muted text)
  white:    [255, 255, 255],
};

const PAGE_W = 210; // A4 mm
const PAGE_H = 297;
const ML = 18;     // margin left
const MR = 18;     // margin right
const CW = PAGE_W - ML - MR; // content width

// Rasterise an SVG from /public onto a canvas and return a PNG data-URL.
// Fetches as text and injects explicit width/height so the browser renders
// at the correct size (SVGs with only viewBox default to 300×150 on canvas).
// Uses a Blob URL to avoid canvas security restrictions.
// bgRgb pre-fills the canvas so jsPDF never sees transparent pixels.
async function loadLogo(svgPath, pxW, pxH, bgRgb) {
  try {
    const res = await fetch(svgPath);
    if (!res.ok) return null;
    const svg = (await res.text()).replace('<svg ', `<svg width="${pxW}" height="${pxH}" `);
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    return new Promise(resolve => {
      const img = new window.Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width  = pxW;
          canvas.height = pxH;
          const ctx = canvas.getContext('2d');
          if (bgRgb) {
            ctx.fillStyle = `rgb(${bgRgb.join(',')})`;
            ctx.fillRect(0, 0, pxW, pxH);
          }
          ctx.drawImage(img, 0, 0, pxW, pxH);
          resolve(canvas.toDataURL('image/png'));
        } catch {
          resolve(null);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });
  } catch {
    return null;
  }
}

function buildPDF({ gateData, scores, overallLevel, overallPct, content, route, displayRoute, meta, logoCover, logoHeader }) {
  const doc  = new jsPDF({ unit: 'mm', format: 'a4' });
  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  let y = 0;

  // ── Design system ──────────────────────────────────────────────────────────
  // Margins
  const M  = 20;          // left & right margin
  const CW = PAGE_W - M * 2; // 170mm content width
  const BOTTOM = PAGE_H - 18; // lowest safe y for content

  // Spacing scale (multiples of 4mm)
  const SP = { xs: 2, sm: 4, md: 8, lg: 12, xl: 16 };

  // Line height: 1.5× cap height ≈ pt × 0.5mm — gives readable spacing
  const lh = pt => pt * 0.5;

  // ── Core helpers ───────────────────────────────────────────────────────────

  function fillBg() {
    doc.setFillColor(...PDF.bg);
    doc.rect(0, 0, PAGE_W, PAGE_H, 'F');
  }

  function pageHeader() {
    doc.setFillColor(...PDF.teal);
    doc.rect(0, 0, PAGE_W, 0.8, 'F');
    // Logo: 24mm wide, height sized to wordmark aspect (992.51/241.09 ≈ 4.117)
    const HLW = 24, HLH = 24 / (992.51 / 241.09); // ≈ 5.83mm
    if (logoHeader) {
      doc.addImage(logoHeader, 'PNG', M, 5, HLW, HLH);
    } else {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...PDF.teal);
      doc.text('COLLABO XD', M, 9);
    }
    // Vertically center text with logo: logoTop + logoH/2 + half-cap-height
    const textY = 5 + HLH / 2 + 1.3;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...PDF.textMut);
    doc.text('·  AI Readiness Report', M + HLW + 3, textY);
    doc.text(`${gateData.firstName}  ·  ${date}`, M + CW, textY, { align: 'right' });
    doc.setDrawColor(...PDF.border);
    doc.setLineWidth(0.2);
    doc.line(M, 14, M + CW, 14);
    y = 22;
  }

  function newPage() {
    doc.addPage();
    fillBg();
    pageHeader();
  }

  function ensureSpace(needed) {
    if (y + needed > BOTTOM) { newPage(); return true; }
    return false;
  }

  // Horizontal rule — draws at current y, advances y by SP.sm
  function rule(color = PDF.border, weight = 0.2) {
    doc.setDrawColor(...color);
    doc.setLineWidth(weight);
    doc.line(M, y, M + CW, y);
    y += SP.sm;
  }

  // Section eyebrow label — draws text, advances y by SP.md
  function kicker(text, color = PDF.teal) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...color);
    doc.text(text.toUpperCase(), M, y);
    y += SP.md;
  }

  // Render text block; returns line count. Caller must advance y.
  function txt(text, pt, color, style = 'normal', maxW = CW, x = M) {
    doc.setFont('helvetica', style);
    doc.setFontSize(pt);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, maxW);
    doc.text(lines, x, y);
    return lines.length;
  }

  // Pill badge — centered text inside a rounded rect
  function pill(text, fillRgb, textRgb, px, py, w = 32) {
    const h = 5.5;
    doc.setFillColor(...fillRgb);
    doc.roundedRect(px, py - 4, w, h, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...textRgb);
    doc.text(text, px + w / 2, py - 0.5, { align: 'center' });
  }

  // Progress bar: track + fill
  function bar(px, py, w, h, pct, fillRgb) {
    doc.setFillColor(...PDF.border);
    doc.roundedRect(px, py, w, h, h / 2, h / 2, 'F');
    if (pct > 0) {
      doc.setFillColor(...fillRgb);
      const filled = Math.max(w * (pct / 100), h);
      doc.roundedRect(px, py, filled, h, h / 2, h / 2, 'F');
    }
  }

  // ── Shared derived values ──────────────────────────────────────────────────
  const overallTier  = TIER[overallLevel];
  const tierRgb      = hexToRgb(overallTier.bar)  || PDF.teal;   // vibrant — for bar fills
  const tierTextRgb  = hexToRgb(overallTier.text) || tierRgb;    // darker — for text labels
  const strongCount  = DIM_CONFIG.filter(d => getLevel(scores[d.key], d.max) === 'strong').length;

  // ── PAGE 1: Cover ──────────────────────────────────────────────────────────
  fillBg();

  // Top accent stripe
  doc.setFillColor(...PDF.teal);
  doc.rect(0, 0, PAGE_W, 1.5, 'F');

  // ── Cover header ────────────────────────────────────────────────────────────
  // Logo: SVG viewBox 992.51×241.09 → 36mm wide, height sized to aspect (≈ 8.74mm)
  const LOGO_W = 36, LOGO_H = 36 / (992.51 / 241.09);
  y = 10;
  if (logoCover) {
    doc.addImage(logoCover, 'PNG', M, y, LOGO_W, LOGO_H);
  } else {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...PDF.teal);
    doc.text('COLLABO XD', M, y + 5);
  }
  // Vertically center text with logo: logoTop + logoH/2 + half-cap-height (8.5pt ≈ 1.5mm)
  const coverTextY = y + LOGO_H / 2 + 1.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...PDF.textMut);
  doc.text('AI Readiness Report', M + LOGO_W + 4, coverTextY);
  doc.text(date, M + CW, coverTextY, { align: 'right' });
  y = y + LOGO_H + SP.sm;
  rule();
  y += SP.md;                        // breathing room below the rule before the headline

  // ── Two-column hero: headline/subtitle (left) | score (right) ──────────────

  // Headline
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...PDF.textPri);
  const hlLines = doc.splitTextToSize(HERO_COPY[overallLevel].headline(gateData.firstName), CW * 0.75);
  doc.text(hlLines, M, y);
  y += hlLines.length * lh(22) + SP.sm;

  // Subtitle
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...PDF.textSec);
  const subLines = doc.splitTextToSize(HERO_COPY[overallLevel].subtitle(strongCount), CW);
  doc.text(subLines, M, y);
  y += subLines.length * lh(9.5) + SP.md;

  // Score inline: "31 / 44  points scored"
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...PDF.textPri);
  doc.text(`${scores.total} / 44`, M, y);
  const scoreInlineW = doc.getTextWidth(`${scores.total} / 44`);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(22);
  doc.setTextColor(...PDF.textMut);
  doc.text('  points scored', M + scoreInlineW, y);
  y += lh(22) + SP.sm;

  // Tier pill
  pill(`${overallTier.label} overall`, tierRgb.map(c => Math.round(c * 0.2 + PDF.surface[0] * 0.8)), tierTextRgb, M, y + 4, 40);
  y += SP.lg;
  rule();

  // ── Overall readiness bar ──────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...PDF.textMut);
  doc.text('OVERALL READINESS', M, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...tierTextRgb);
  doc.text(`${overallPct}%`, M + CW, y, { align: 'right' });
  y += SP.sm;
  bar(M, y, CW, 3.5, overallPct, tierRgb);
  y += SP.lg;
  rule();

  // ── 4-column dimension summary ─────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...PDF.textMut);
  doc.text('DIMENSION SCORES', M, y);
  y += SP.md;

  const COL = CW / 4;
  DIM_CONFIG.forEach((dim, i) => {
    const sc      = scores[dim.key];
    const lv      = getLevel(sc, dim.max);
    const tr      = DIM_SEMANTICS[dim.key][lv];
    const pct     = Math.round((sc / dim.max) * 100);
    const rgbBar  = hexToRgb(tr.bar)  || PDF.teal;
    const rgbText = hexToRgb(tr.text) || rgbBar;
    const cx      = M + i * COL;

    // dim label
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...PDF.textMut);
    doc.text(dim.shortLabel, cx, y);
    // pct
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...rgbText);
    doc.text(`${pct}%`, cx, y + 6.5);
    // score / max
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...PDF.textMut);
    doc.text(`${sc} / ${dim.max} pts`, cx, y + 12);
    // tier label
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...rgbText);
    doc.text(tr.label, cx, y + 16.5);
    // mini bar
    bar(cx, y + 19, COL - 8, 2.5, pct, rgbBar);
  });
  y += 26;
  rule();

  // ── Cover footer (pinned) ──────────────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...PDF.textMut);
  doc.text(`Prepared for: ${gateData.firstName}  ·  ${gateData.orgType}  ·  collaboxd.com`, M, y);

  // ── PAGE 2: Score Breakdown ────────────────────────────────────────────────
  newPage();
  kicker('Score Breakdown');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...PDF.textSec);
  doc.text('How you scored across each of the four AI readiness dimensions.', M, y);
  y += SP.lg;

  DIM_CONFIG.forEach(dim => {
    const sc      = scores[dim.key];
    const lv      = getLevel(sc, dim.max);
    const tr      = DIM_SEMANTICS[dim.key][lv];
    const block   = content.dimensions[dim.key][lv];
    const pct     = Math.round((sc / dim.max) * 100);
    const rgbBar  = hexToRgb(tr.bar)  || PDF.teal;
    const rgbText = hexToRgb(tr.text) || rgbBar;
    const CARD_H  = 34;

    ensureSpace(CARD_H + SP.sm);

    // Card background + left accent bar
    doc.setFillColor(...PDF.surface);
    doc.setDrawColor(...PDF.border);
    doc.setLineWidth(0.2);
    doc.roundedRect(M, y, CW, CARD_H, 2.5, 2.5, 'FD');
    doc.setFillColor(...rgbBar);
    doc.roundedRect(M, y, 3, CARD_H, 1.5, 1.5, 'F');

    const IX = M + 10;           // inner content x
    const IW = CW - 10;          // inner content width
    const RX = M + CW - 5;       // right-align anchor

    // Row 1 — dim label (left) | pct (right)
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...PDF.textPri);
    doc.text(dim.label, IX, y + 9);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...rgbText);
    doc.text(`${pct}%`, RX, y + 9, { align: 'right' });

    // Row 2 — tier pill (left) | score (right)
    // Auto-size pill width to fit the label text
    doc.setFontSize(7);
    const pillW = doc.getTextWidth(tr.label) + 8;
    pill(tr.label, rgbBar.map(c => Math.round(c * 0.2 + PDF.surface[0] * 0.8)), rgbText, IX, y + 15, pillW);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...PDF.textMut);
    doc.text(`${sc} / ${dim.max} pts`, RX, y + 15, { align: 'right' });

    // Row 3 — progress bar
    bar(IX, y + 19, IW - 5, 2.5, pct, rgbBar);

    // Row 4 — headline
    const hlArr = doc.splitTextToSize(block.headline, IW - 5);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...PDF.textSec);
    doc.text(hlArr[0] || '', IX, y + 28);

    y += CARD_H + SP.sm;
  });

  // ── PAGES 3+: Dimension Analyses ──────────────────────────────────────────
  newPage();
  kicker('Readiness Analysis');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...PDF.textSec);
  doc.text('A full breakdown of what your scores mean and what to do about them.', M, y);
  y += SP.lg;

  DIM_CONFIG.forEach((dim, idx) => {
    const sc      = scores[dim.key];
    const lv      = getLevel(sc, dim.max);
    const block   = content.dimensions[dim.key][lv];
    const tr      = DIM_SEMANTICS[dim.key][lv];
    const rgbBar  = hexToRgb(tr.bar)  || PDF.teal;
    const rgbText = hexToRgb(tr.text) || rgbBar;
    const pct     = Math.round((sc / dim.max) * 100);

    ensureSpace(SP.xl + lh(12));

    // Dim label + score inline
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...rgbText);
    doc.text(dim.label.toUpperCase(), M, y);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...PDF.textMut);
    doc.text(`${sc} / ${dim.max}  ·  ${pct}%`, M + CW, y, { align: 'right' });
    y += SP.sm;

    // Pill then bar on the next line
    pill(tr.label, rgbBar.map(c => Math.round(c * 0.2 + PDF.surface[0] * 0.8)), rgbText, M, y + 4, 30);
    y += SP.md;
    bar(M, y, CW, 2.5, pct, rgbBar);
    y += SP.md;

    // Headline
    ensureSpace(lh(12) + SP.sm);
    const hn = txt(block.headline, 12, PDF.textPri, 'bold');
    y += hn * lh(12) + SP.sm;

    // Body paragraphs
    block.body.split('\n\n').forEach(para => {
      ensureSpace(lh(9) * 3 + SP.sm);
      const pn = txt(para, 9, PDF.textSec);
      y += pn * lh(9) + SP.sm;
    });

    // Section divider (not after last dim)
    if (idx < DIM_CONFIG.length - 1) {
      y += SP.sm;
      ensureSpace(SP.xl);
      rule(PDF.border, 0.3);
      y += SP.sm;
    }
  });

  y += SP.sm;
  rule(PDF.teal, 0.4);

  // ── Insight ────────────────────────────────────────────────────────────────
  ensureSpace(SP.xl + lh(13));
  kicker('What You Might Not Expect');

  const ihn = txt(content.insight.headline, 13, PDF.teal, 'bold');
  y += ihn * lh(13) + SP.md;

  content.insight.body.split('\n\n').forEach(para => {
    ensureSpace(lh(9) * 3 + SP.sm);
    const pn = txt(para, 9, PDF.textSec);
    y += pn * lh(9) + SP.sm;
  });

  y += SP.sm;
  rule(PDF.teal, 0.4);

  // ── Action Steps ───────────────────────────────────────────────────────────
  ensureSpace(SP.xl + lh(10));
  kicker('Three Things to Do This Week');

  const steps = content.actionableSteps[route] || content.actionableSteps.discovery;
  steps.forEach((step, i) => {
    ensureSpace(SP.xl + lh(10));

    // Step number badge
    doc.setFillColor(...PDF.surface);
    doc.roundedRect(M, y - 4, 8, 8, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...PDF.teal);
    doc.text(`${i + 1}`, M + 4, y, { align: 'center' });

    // Title
    const shn = txt(step.title, 10, PDF.textPri, 'bold', CW - 12, M + 12);
    y += shn * lh(10) + SP.xs;

    // Body
    const sbn = txt(step.body, 9, PDF.textSec, 'normal', CW - 12, M + 12);
    y += sbn * lh(9) + SP.md;
  });

  rule(PDF.border, 0.3);

  // ── Stats ──────────────────────────────────────────────────────────────────
  ensureSpace(SP.xl + lh(9));
  kicker('For Context');

  const statColW = (CW - SP.md) / 2;
  const statStartY = y;

  content.stats.headlines.slice(0, 2).forEach((stat, i) => {
    const sx = i === 0 ? M : M + statColW + SP.md;
    let sy = statStartY;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(...PDF.teal);
    doc.text(stat.figure, sx, sy);
    sy += lh(20) + SP.xs;

    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...PDF.textSec);
    const lb = doc.splitTextToSize(stat.label, statColW);
    doc.text(lb, sx, sy);
    sy += lb.length * lh(9) + SP.xs;

    doc.setFont('helvetica', 'italic'); doc.setFontSize(7.5); doc.setTextColor(...PDF.textMut);
    doc.text(stat.source, sx, sy);
  });

  y = statStartY + lh(20) + SP.xs + 2 * lh(9) + SP.xs + lh(7.5) + SP.lg;
  rule(PDF.border, 0.3);

  // ── CTA ────────────────────────────────────────────────────────────────────
  const whenReady = content.whenReady[displayRoute];
  ensureSpace(SP.xl + lh(13));
  kicker('Your Recommended Next Step');

  doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...PDF.accent);
  doc.text(meta.label.toUpperCase(), M, y);
  y += SP.md;

  const chn = txt(whenReady.headline, 13, PDF.textPri, 'bold');
  y += chn * lh(13) + SP.md;

  whenReady.body.split('\n\n').forEach(para => {
    ensureSpace(lh(9) * 3 + SP.sm);
    const pn = txt(para, 9, PDF.textSec);
    y += pn * lh(9) + SP.sm;
  });

  // ── Schedule button ────────────────────────────────────────────────────────
  const btnW = 72;
  const btnH = 9;
  const btnX = M;
  ensureSpace(SP.md + btnH + SP.sm);
  y += SP.md;
  // Button fill
  doc.setFillColor(...PDF.teal);
  doc.roundedRect(btnX, y, btnW, btnH, 2, 2, 'F');
  // Button label
  const btnLabel = whenReady.ctaText || 'Schedule a Conversation';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...PDF.bg);
  doc.text(btnLabel, btnX + btnW / 2, y + btnH / 2 + 1, { align: 'center' });
  // Clickable link overlay
  doc.link(btnX, y, btnW, btnH, { url: meta.bookingUrl });
  y += btnH + SP.sm;

  // ── Final footer ───────────────────────────────────────────────────────────
  ensureSpace(SP.lg);
  y += SP.sm;
  rule(PDF.teal, 0.4);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...PDF.textMut);
  doc.text('collaboxd.com', M, y);
  doc.text(`${gateData.firstName}  ·  AI Readiness Report  ·  ${date}`, M + CW, y, { align: 'right' });

  return doc;
}

function hexToRgb(hex) {
  if (!hex || !hex.startsWith('#')) return null;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

function downloadReport(doc, firstName) {
  doc.save(`collaboxd-ai-readiness-report-${firstName.toLowerCase().replace(/\s+/g, '-')}.pdf`);
}

// ── Main component ──────────────────────────────────────────────────────────

export default function ResultsScreen({ answers, gateData, onRetake }) {
  const scores      = getSectionScores(answers);
  const route       = getRoute(scores);
  const overallLevel = getOverallLevel(scores.total);
  const overallPct  = Math.round((scores.total / 44) * 100);
  const content     = CONTENT[gateData.orgType] || CONTENT.smallBusiness;
  const displayRoute = gateData.orgType === 'government' ? 'discovery' : route;
  const meta        = ROUTE_META[displayRoute];
  const narrow      = useIsNarrow();

  useEffect(() => {
    track('results_viewed', { route, overallLevel, orgType: gateData.orgType });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const strongCount  = DIM_CONFIG.filter(d => getLevel(scores[d.key], d.max) === 'strong').length;
  const overallTier  = TIER[overallLevel];
  const steps        = content.actionableSteps[route] || content.actionableSteps.discovery;
  const whenReady    = content.whenReady[displayRoute];

  const pad = narrow ? '24px 20px' : '28px 32px';

  async function handleDownload() {
    const [logoCover, logoHeader] = await Promise.all([
      loadLogo('/collaboxd-logo.svg', 2880, 699, PDF.bg),
      loadLogo('/collaboxd-logo.svg', 1920, 466, PDF.bg),
    ]);
    const doc = buildPDF({
      gateData, scores, overallLevel, overallPct,
      content, route, displayRoute, meta,
      logoCover, logoHeader,
    });
    downloadReport(doc, gateData.firstName);
    track('report_downloaded', { route, overallLevel, orgType: gateData.orgType });
  }

  return (
    <div style={{ background: COLORS.bgApp, minHeight: '100vh', padding: '0 20px', boxSizing: 'border-box' }}>
      <main id="main-content" style={{ maxWidth: 720, margin: '0 auto', padding: '28px 0 64px' }}>

        {/* ── 1. Brand line ─────────────────────────────────────────────── */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '0 0 24px',
        }}>
          <p style={{
            fontFamily: FONTS.body,
            fontSize: 15,
            color: COLORS.textMuted,
            margin: 0,
            letterSpacing: '0.03em',
          }}>
            Collabo XD · AI Readiness Report
          </p>
          <button
            onClick={handleDownload}
            className="btn-outline"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'none',
              border: `1px solid ${COLORS.borderButton}`,
              borderRadius: 8,
              fontFamily: FONTS.body,
              fontSize: 17,
              fontWeight: 500,
              color: COLORS.textSecondary,
              cursor: 'pointer',
              padding: '13px 22px',
            }}
          >
            <Icon name="download" size={16} style={{ color: COLORS.textSecondary }} />
            Download report
          </button>
        </div>

        {/* ── 2. Hero block ─────────────────────────────────────────────── */}
        <div style={{
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          padding: pad,
          marginBottom: 24,
        }}>
          {/* Top row: headline left, score right */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: narrow ? '1fr' : '1fr auto',
            gap: narrow ? 20 : 40,
            alignItems: 'center',
            marginBottom: 24,
          }}>
            {/* Left: headline + subtitle + pill */}
            <div>
              <h1 style={{
                fontFamily: FONTS.body,
                fontSize: 26,
                fontWeight: 500,
                color: COLORS.textPrimary,
                margin: '0 0 10px',
                lineHeight: 1.3,
                letterSpacing: TRACKING.tightHeadline,
              }}>
                {HERO_COPY[overallLevel].headline(gateData.firstName)}
              </h1>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 17,
                lineHeight: 1.65,
                color: COLORS.textSecondary,
                margin: '0 0 16px',
                maxWidth: 380,
              }}>
                {HERO_COPY[overallLevel].subtitle(strongCount)}
              </p>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: overallTier.fill,
                color: overallTier.text,
                fontFamily: FONTS.body,
                fontSize: 15,
                fontWeight: 500,
                padding: '4px 12px',
                borderRadius: 100,
                border: `1px solid ${overallTier.bar}40`,
              }}>
                <span style={{
                  width: 6, height: 6,
                  borderRadius: '50%',
                  background: overallTier.bar,
                  flexShrink: 0,
                }} />
                {overallTier.label} overall
              </span>
            </div>

            {/* Right: score number */}
            <div style={{ textAlign: narrow ? 'left' : 'right' }}>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 44,
                fontWeight: 500,
                color: COLORS.textPrimary,
                margin: '0 0 4px',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {scores.total}
                <span style={{ fontSize: 21, color: COLORS.textMuted, fontWeight: 400 }}> / 44</span>
              </p>
              <p style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted, margin: 0 }}>
                points scored
              </p>
            </div>
          </div>

          {/* Full-width overall score bar */}
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 8,
            }}>
              <span style={{
                fontFamily: FONTS.body,
                fontSize: 14,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: TRACKING.kicker,
                color: COLORS.textMuted,
              }}>
                Overall readiness
              </span>
              <span style={{
                fontFamily: FONTS.body,
                fontSize: 16,
                fontWeight: 500,
                color: overallTier.text,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {overallPct}%
              </span>
            </div>
            <div
              role="progressbar"
              aria-valuenow={overallPct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Overall AI readiness: ${overallPct}%`}
              style={{
                height: 8,
                background: 'rgba(37,47,106,0.10)',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <div style={{
                height: '100%',
                width: `${overallPct}%`,
                background: overallTier.bar,
                borderRadius: 4,
                transition: 'width 0.8s ease',
              }} />
            </div>
          </div>
        </div>

        {/* ── 3. Score breakdown ────────────────────────────────────────── */}
        <Eyebrow>Score breakdown</Eyebrow>

        <div style={{
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          padding: '20px 24px',
          marginBottom: 24,
        }}>
          <GroupedBarChart scores={scores} narrow={narrow} />
        </div>

        {/* ── 4. Recommended route ─────────────────────────────────────── */}
        <Eyebrow>Recommended for you</Eyebrow>

        <div style={{
          background: COLORS.surface,
          border: `1px solid ${meta.color}33`,
          borderLeft: `3px solid ${meta.color}`,
          borderRadius: 12,
          padding: pad,
          marginBottom: 24,
          display: 'flex',
          flexDirection: narrow ? 'column' : 'row',
          alignItems: narrow ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: 20,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{
                fontFamily: FONTS.body,
                fontSize: 16,
                fontWeight: 500,
                color: meta.color,
                letterSpacing: '0.01em',
              }}>
                {meta.label}
              </span>
            </div>
            <p style={{
              fontFamily: FONTS.body,
              fontSize: 19,
              fontWeight: 500,
              color: COLORS.textPrimary,
              margin: '0 0 6px',
              lineHeight: 1.35,
            }}>
              {meta.headline}
            </p>
            <p style={{
              fontFamily: FONTS.body,
              fontSize: 16,
              lineHeight: 1.6,
              color: COLORS.textSecondary,
              margin: 0,
              maxWidth: 460,
            }}>
              {meta.subhead}
            </p>
          </div>
        </div>

        {/* ── 5. Category cards ─────────────────────────────────────────── */}
        <Eyebrow>Your readiness breakdown</Eyebrow>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginBottom: 24,
        }}>
          {DIM_CONFIG.map(dim => {
            const score = scores[dim.key];
            const level = getLevel(score, dim.max);
            const block = content.dimensions[dim.key][level];
            return (
              <CategoryCard key={dim.key} dim={dim} score={score} level={level} block={block} />
            );
          })}
        </div>

        {/* ── 6. Insight callout ────────────────────────────────────────── */}
        <div style={{
          background: 'rgba(42,192,208,0.06)',
          border: '1px solid rgba(42,192,208,0.15)',
          borderRadius: 12,
          padding: pad,
          marginBottom: 24,
        }}>
          <h2 style={{
            fontFamily: FONTS.body,
            fontSize: 15,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: TRACKING.kicker,
            color: COLORS.holidayBlue,
            margin: '0 0 10px',
          }}>
            What you might not expect
          </h2>
          <p style={{
            fontFamily: FONTS.body,
            fontSize: 20,
            fontWeight: 500,
            color: COLORS.holidayBlue,
            margin: '0 0 12px',
            lineHeight: 1.35,
          }}>
            {content.insight.headline}
          </p>
          <p style={{
            fontFamily: FONTS.body,
            fontSize: 17,
            lineHeight: 1.65,
            color: COLORS.textSecondary,
            margin: 0,
          }}>
            {firstParagraph(content.insight.body)}
          </p>
        </div>

        {/* ── 7-8. Action steps ─────────────────────────────────────────── */}
        <Eyebrow>Three things to do this week</Eyebrow>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 12,
          marginBottom: 24,
        }}>
          {steps.map((step, i) => (
            <div key={i} style={{
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 12,
              padding: '16px 20px',
            }}>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 15,
                fontWeight: 500,
                color: COLORS.textMuted,
                margin: '0 0 10px',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {String(i + 1).padStart(2, '0')}
              </p>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 17,
                fontWeight: 500,
                color: COLORS.textPrimary,
                margin: '0 0 8px',
                lineHeight: 1.4,
              }}>
                {step.title}
              </p>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 16,
                lineHeight: 1.6,
                color: COLORS.textSecondary,
                margin: 0,
              }}>
                {step.body}
              </p>
            </div>
          ))}
        </div>

        {/* ── 9-10. Stats ───────────────────────────────────────────────── */}
        <Eyebrow>For context</Eyebrow>

        <div style={{
          display: 'grid',
          gridTemplateColumns: narrow ? '1fr' : '1fr 1fr',
          gap: 12,
          marginBottom: 24,
        }}>
          {content.stats.headlines.slice(0, 2).map((stat, i) => (
            <div key={i} style={{
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 12,
              padding: '16px 20px',
            }}>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 32,
                fontWeight: 500,
                color: COLORS.holidayBlue,
                margin: '0 0 8px',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {stat.figure}
              </p>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 16,
                lineHeight: 1.5,
                color: COLORS.textSecondary,
                margin: '0 0 6px',
              }}>
                {stat.label}
              </p>
              <p style={{
                fontFamily: FONTS.body,
                fontSize: 14,
                fontStyle: 'italic',
                color: COLORS.textMuted,
                margin: 0,
              }}>
                {stat.source}
              </p>
            </div>
          ))}
        </div>

        {/* ── 11. CTA block ─────────────────────────────────────────────── */}
        <div style={{
          background: COLORS.deepBlue,
          borderRadius: 12,
          padding: narrow ? '28px 20px' : '32px 36px',
          marginBottom: 32,
        }}>
          <h2 style={{
            fontFamily: FONTS.body,
            fontSize: 21,
            fontWeight: 500,
            color: COLORS.white,
            margin: '0 0 10px',
            lineHeight: 1.3,
          }}>
            {whenReady.headline}
          </h2>
          <p style={{
            fontFamily: FONTS.body,
            fontSize: 17,
            lineHeight: 1.65,
            color: 'rgba(255,255,255,0.6)',
            margin: '0 0 24px',
          }}>
            {firstParagraph(whenReady.body)}
          </p>
          <a
            href={meta.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('cta_clicked', { route: displayRoute, ctaText: whenReady.ctaText })}
            className="btn-solid-light"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: COLORS.white,
              color: COLORS.deepBlue,
              fontFamily: FONTS.body,
              fontSize: 17,
              fontWeight: 500,
              padding: '13px 22px',
              borderRadius: 8,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {whenReady.ctaText}
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </div>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <footer style={{
          borderTop: `1px solid ${COLORS.border}`,
          paddingTop: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 10,
        }}>
          <p style={{ fontFamily: FONTS.body, fontSize: 15, color: COLORS.textMuted, margin: 0 }}>
            © 2026 Collabo Creative LLC, DBA Collabo XD
          </p>
          <button
              onClick={onRetake}
              className="btn-ghost"
              style={{
                background: 'none',
                border: 'none',
                fontFamily: FONTS.body,
                fontSize: 16,
                color: COLORS.holidayBlue,
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
                padding: 0,
              }}
            >
              Retake the assessment
            </button>
        </footer>

      </main>
    </div>
  );
}

// ── Category card ───────────────────────────────────────────────────────────

function CategoryCard({ dim, score, level, block }) {
  const [open, setOpen] = useState(false);
  const tier    = DIM_SEMANTICS[dim.key][level];
  const pct     = Math.round((score / dim.max) * 100);
  const panelId = `analysis-${dim.key}`;

  return (
    <div style={{
      background: COLORS.surface,
      border: `1px solid ${open ? tier.bar + '55' : COLORS.border}`,
      borderRadius: 12,
      overflow: 'hidden',
      transition: 'border-color 0.2s ease',
    }}>
      {/* Clickable header — the entire header row is the toggle */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className="btn-card"
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          padding: '16px 20px',
          display: 'block',
        }}
      >
        {/* Row 1: label + score + chevron */}
        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontFamily: FONTS.body, fontSize: 18, fontWeight: 500, color: COLORS.textPrimary }}>
            {dim.label}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <span
                style={{ fontFamily: FONTS.body, fontSize: 18, fontWeight: 500, color: tier.text, fontVariantNumeric: 'tabular-nums' }}
                aria-label={`${pct}%, ${score} out of ${dim.max} points`}
              >
                {pct}%
              </span>
              <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                {score} / {dim.max} pts
              </span>
            </span>
            <Icon
              name={open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
              size={20}
              style={{ color: COLORS.textMuted }}
            />
          </span>
        </span>

        {/* Progress bar */}
        <span
          role="img"
          aria-label={`${tier.label} — ${pct}% of maximum`}
          style={{ display: 'block', height: 6, background: 'rgba(37,47,106,0.10)', borderRadius: 3, overflow: 'hidden', marginBottom: 12 }}
        >
          <span style={{ display: 'block', height: '100%', width: `${pct}%`, background: tier.bar, borderRadius: 3, transition: 'width 0.5s ease' }} />
        </span>

        {/* Tier badge + headline on same row */}
        <span style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
          <span style={{
            background: tier.fill,
            color: tier.text,
            fontFamily: FONTS.body,
            fontSize: 14,
            fontWeight: 500,
            padding: '2px 8px',
            borderRadius: 100,
            flexShrink: 0,
            marginTop: 2,
          }}>
            {tier.label}
          </span>
          <span style={{ display: 'block', fontFamily: FONTS.body, fontSize: 17, fontWeight: 500, color: COLORS.textPrimary, lineHeight: 1.4, flex: 1 }}>
            {block.headline}
          </span>
        </span>

        {/* Preview — 2-line clamp with auto ellipsis, collapsed only */}
        {!open && (
          <span style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontFamily: FONTS.body,
            fontSize: 16,
            lineHeight: 1.6,
            color: COLORS.textSecondary,
            marginTop: 10,
          }}>
            {block.body.split('\n\n')[0]}
          </span>
        )}
      </button>

      {/* Expandable body */}
      <div id={panelId} hidden={!open}>
        <div style={{
          padding: '0 20px 20px',
          borderTop: `1px solid ${COLORS.border}`,
          paddingTop: 16,
        }}>
          {block.body.split('\n\n').map((para, i) => (
            <p key={i} style={{
              fontFamily: FONTS.body,
              fontSize: 17,
              lineHeight: 1.65,
              color: COLORS.textSecondary,
              margin: i === 0 ? 0 : '12px 0 0',
            }}>
              {para}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Grouped bar chart ───────────────────────────────────────────────────────

function GroupedBarChart({ scores, narrow }) {
  const [hovered, setHovered] = useState(null); // dim key
  const MAX_Y    = 12;
  const CHART_H  = 130;
  const Y_AXIS_W = 28;

  const hoveredIdx = hovered ? DIM_CONFIG.findIndex(d => d.key === hovered) : -1;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        {/* Y axis */}
        <div style={{ width: Y_AXIS_W, flexShrink: 0, position: 'relative', height: CHART_H }}>
          {[12, 8, 4, 0].map(tick => (
            <span key={tick} style={{
              position: 'absolute',
              bottom: `${(tick / MAX_Y) * 100}%`,
              right: 6,
              transform: 'translateY(50%)',
              fontFamily: FONTS.body,
              fontSize: 13,
              color: COLORS.textMuted,
              lineHeight: 1,
            }}>
              {tick}
            </span>
          ))}
        </div>

        {/* Chart area — overflow:visible so tooltip can poke above */}
        <div style={{ flex: 1, height: CHART_H, position: 'relative', display: 'flex', alignItems: 'flex-end', overflow: 'visible' }}>

          {/* Tooltip */}
          {hovered && hoveredIdx >= 0 && (() => {
            const dim   = DIM_CONFIG[hoveredIdx];
            const score = scores[dim.key];
            const tier  = DIM_SEMANTICS[dim.key][getLevel(score, dim.max)];
            const pct   = Math.round((score / dim.max) * 100);
            // Clamp left so tooltip stays inside chart on edge groups
            const leftPct = Math.min(Math.max((hoveredIdx + 0.5) * 25, 14), 86);
            return (
              <div style={{
                position: 'absolute',
                left: `${leftPct}%`,
                top: -10,
                transform: 'translate(-50%, -100%)',
                background: COLORS.surfaceHigh,
                border: `1px solid ${COLORS.border}`,
                borderLeft: `3px solid ${tier.bar}`,
                borderRadius: 8,
                padding: '10px 14px',
                pointerEvents: 'none',
                zIndex: 10,
                whiteSpace: 'nowrap',
                boxShadow: '0 6px 18px rgba(37,47,106,0.15)',
              }}>
                <p style={{
                  fontFamily: FONTS.body,
                  fontSize: 15,
                  fontWeight: 500,
                  color: COLORS.textPrimary,
                  margin: '0 0 8px',
                }}>
                  {dim.label}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                    <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted }}>Your score</span>
                    <span style={{ fontFamily: FONTS.body, fontSize: 14, fontWeight: 500, color: tier.text, fontVariantNumeric: 'tabular-nums' }}>
                      {score} / {dim.max}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                    <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted }}>Maximum</span>
                    <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                      {dim.max}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                    <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted }}>Level</span>
                    <span style={{ fontFamily: FONTS.body, fontSize: 14, fontWeight: 500, color: tier.text }}>
                      {tier.label} · {pct}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Horizontal grid lines */}
          {[4, 8, 12].map(tick => (
            <div key={tick} style={{
              position: 'absolute',
              bottom: `${(tick / MAX_Y) * 100}%`,
              left: 0,
              right: 0,
              height: 1,
              background: 'rgba(37,47,106,0.08)',
              pointerEvents: 'none',
            }} />
          ))}

          {/* One group per dimension */}
          {DIM_CONFIG.map(dim => {
            const score     = scores[dim.key];
            const tier      = DIM_SEMANTICS[dim.key][getLevel(score, dim.max)];
            const isHovered = hovered === dim.key;
            return (
              <div
                key={dim.key}
                onMouseEnter={() => setHovered(dim.key)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  flex: 1,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  gap: 3,
                  paddingLeft: narrow ? 4 : 8,
                  paddingRight: narrow ? 4 : 8,
                  cursor: 'default',
                  background: isHovered ? 'rgba(37,47,106,0.04)' : 'transparent',
                  borderRadius: 4,
                  transition: 'background 0.15s ease',
                }}
              >
                {/* Max bar (faded) */}
                <div style={{
                  flex: 1,
                  maxWidth: 16,
                  height: `${(dim.max / MAX_Y) * 100}%`,
                  background: isHovered ? 'rgba(37,47,106,0.22)' : 'rgba(37,47,106,0.14)',
                  borderRadius: '2px 2px 0 0',
                  transition: 'background 0.15s ease',
                }} />
                {/* Score bar (tier-colored) */}
                <div style={{
                  flex: 1,
                  maxWidth: 16,
                  height: `${(score / MAX_Y) * 100}%`,
                  background: tier.bar,
                  borderRadius: '2px 2px 0 0',
                  opacity: isHovered ? 1 : 0.8,
                  transition: 'opacity 0.15s ease',
                }} />
              </div>
            );
          })}
        </div>
      </div>

      {/* X-axis labels — dim slightly when another is hovered */}
      <div style={{ display: 'flex', paddingLeft: Y_AXIS_W, marginTop: 8 }}>
        {DIM_CONFIG.map(dim => {
          const score     = scores[dim.key];
          const tier      = DIM_SEMANTICS[dim.key][getLevel(score, dim.max)];
          const isHovered = hovered === dim.key;
          return (
            <div key={dim.key} style={{
              flex: 1,
              paddingLeft: narrow ? 4 : 8,
              paddingRight: narrow ? 4 : 8,
              textAlign: 'center',
              opacity: hovered && !isHovered ? 0.45 : 1,
              transition: 'opacity 0.15s ease',
            }}>
              <div style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.textMuted, lineHeight: 1.35, marginBottom: 3 }}>
                {narrow ? dim.shortLabel : dim.label}
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 16, fontWeight: 500, color: tier.text, fontVariantNumeric: 'tabular-nums' }}>
                {Math.round((score / dim.max) * 100)}%
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                {score}/{dim.max} pts
              </div>
            </div>
          );
        })}
      </div>

      {/* Custom legend */}
      <div style={{
        display: 'flex',
        gap: 20,
        paddingLeft: Y_AXIS_W,
        marginTop: 14,
        paddingTop: 12,
        borderTop: `1px solid ${COLORS.border}`,
      }}>
        <LegendItem color="rgba(37,47,106,0.14)" label="Maximum possible" />
        <LegendItem color={COLORS.accent}        label="Your score" />
      </div>
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 14, height: 6, background: color, borderRadius: 2, flexShrink: 0 }} />
      <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted }}>{label}</span>
    </div>
  );
}

// ── Shared ──────────────────────────────────────────────────────────────────

// Renders as <h2> so screen readers can navigate the page by headings.
// Visual styles match the design's kicker/eyebrow treatment.
function Eyebrow({ children }) {
  return (
    <h2 style={{
      fontFamily: FONTS.body,
      fontSize: 15,
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: TRACKING.kicker,
      color: COLORS.textMuted,
      margin: '0 0 12px',
    }}>
      {children}
    </h2>
  );
}
